/*
  Herbies cart: THE add-to-cart for product pages.
  ================================================
  One click = the paid line through /cart/add.js (the one AddToCart Shopify
  reports for it), then the free bundle units and tier gifts added to the same
  cart by the bundles app (/api/cart-extras, server-side, so the browser-side
  tracking does not count them as extra add-to-carts), then straight to
  checkout (or the drawer for templates set to "add to cart"). If the app call
  fails, the browser adds the missing extras itself. Nothing patches
  window.fetch and nothing adds lines later, so there is no race with the
  redirect.

  Line contract with the discount Function (bundle-bxgy in the bundles app),
  do not rename:
    _bundle_free / _bundle_min_paid  free units of a BOGO tier, a one-time line
    _bundle_gift                     a tier gift (made free by the Function)
    _bundle_host / _bundle_tier      a bundle card that adds its own product
                                     (Detox Bundle), counted as the top tier

  Gift config comes from the bundles app (/api/bundle-config), fetched when the
  page loads and cached for this session; a click waits for it (at most a few
  seconds). If it cannot be loaded the order still goes through without the
  gifts. The Function prices everything and never trusts the storefront.

  Removing gifts that no longer qualify (customer lowers the quantity in the
  drawer) is done by the drawer's cleanup in sections/cart-drawer.liquid, using
  HerbiesCart.eligibleGiftIds below. That cleanup only ever removes lines.
*/
(function () {
  'use strict';

  var cfgEl = document.getElementById('herbies-cart-config');
  var SETTINGS = {};
  try { SETTINGS = JSON.parse((cfgEl && cfgEl.textContent) || '{}'); } catch (_e) { SETTINGS = {}; }

  var CACHE_KEY = 'herbies-bundle-config-v1';
  var CACHE_MS = 10 * 60 * 1000;
  var CONFIG_WAIT_MS = 3500;
  var EXTRAS_WAIT_MS = 9000; // the app gives up on Shopify after 6 s

  // ---------------------------------------------------------------------------
  // Bundle config (gifts per tier), loaded once per page and cached per session.
  // ---------------------------------------------------------------------------
  var configPromise = null;

  function readCache() {
    try {
      var raw = sessionStorage.getItem(CACHE_KEY);
      if (!raw) return null;
      var c = JSON.parse(raw);
      if (!c || !c.at || Date.now() - c.at > CACHE_MS || !c.config) return null;
      return c.config;
    } catch (_e) { return null; }
  }

  function writeCache(config) {
    try { sessionStorage.setItem(CACHE_KEY, JSON.stringify({ at: Date.now(), config: config })); } catch (_e) { /* private mode */ }
  }

  function loadConfig() {
    if (configPromise) return configPromise;
    var cached = readCache();
    if (cached) {
      configPromise = Promise.resolve(cached);
      return configPromise;
    }
    if (!SETTINGS.apiBase || !SETTINGS.shop) {
      configPromise = Promise.resolve({ products: [] });
      return configPromise;
    }
    configPromise = fetch(SETTINGS.apiBase + '/api/bundle-config?shop=' + encodeURIComponent(SETTINGS.shop))
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (j) {
        var config = j && Array.isArray(j.products) ? j : null;
        if (config) writeCache(config);
        return config || { products: [] };
      })
      .catch(function () {
        configPromise = null; // try again on the next click
        return { products: [] };
      });
    return configPromise;
  }

  /** The config, or an empty one when it takes longer than `ms`. */
  function configWithin(ms) {
    return Promise.race([
      loadConfig(),
      new Promise(function (resolve) { setTimeout(function () { resolve({ products: [] }); }, ms); }),
    ]);
  }

  function productConfig(config, productId) {
    var list = (config && config.products) || [];
    for (var i = 0; i < list.length; i++) {
      if (String(list[i].id) === String(productId)) return list[i];
    }
    return null;
  }

  function appliesMatch(applies, isSub) {
    if (!applies || applies === 'all') return true;
    if (applies === 'onetime') return !isSub;
    if (applies === 'subscription') return isSub;
    return true;
  }

  /** The heaviest tier of a product (the Detox Bundle always counts as that). */
  function topTier(productCfg) {
    var top = 0;
    ((productCfg && productCfg.tiers) || []).forEach(function (t) {
      var g = Number(t && t.group);
      if (g > top) top = g;
    });
    return top;
  }

  /**
   * Gift variant ids unlocked at `qty` units: every tier reached (qty >= buy +
   * free) adds its gifts, one unit per gift variant, filtered on whether the
   * bundle line is a subscription. Same rules as the Function.
   */
  function giftIdsFor(productCfg, qty, isSub) {
    var out = {};
    ((productCfg && productCfg.tiers) || []).forEach(function (t) {
      if (!t || qty < Number(t.group)) return;
      (t.gifts || []).forEach(function (g) {
        if (g && g.id && appliesMatch(g.applies, isSub)) out[String(g.id)] = true;
      });
    });
    return Object.keys(out);
  }

  /**
   * Which gift variants a cart is entitled to, from its paid, free and host
   * lines. Used by the drawer cleanup to remove gifts that no longer qualify.
   */
  function eligibleGiftIds(cart, config) {
    var perProduct = {};
    (cart.items || []).forEach(function (i) {
      var p = i.properties || {};
      if (p._bundle_gift) return;
      var pid = String(i.product_id);
      var host = p._bundle_host ? String(p._bundle_host) : null;
      var key = host || pid;
      var e = perProduct[key] || (perProduct[key] = { qty: 0, host: false, sub: false });
      if (host) e.host = true;
      else e.qty += i.quantity;
      if (i.selling_plan_allocation) e.sub = true;
    });
    var ids = {};
    Object.keys(perProduct).forEach(function (pid) {
      var cfg = productConfig(config, pid);
      if (!cfg) return;
      var e = perProduct[pid];
      var qty = e.host ? Math.max(e.qty, topTier(cfg)) : e.qty;
      giftIdsFor(cfg, qty, e.sub).forEach(function (id) { ids[id] = true; });
    });
    return ids;
  }

  // ---------------------------------------------------------------------------
  // The add.
  // ---------------------------------------------------------------------------
  var adding = false;

  // A spinner in the button while it works; the colour stays (changing it flashed the button white).
  var STYLE_ID = 'herbies-cart-style';
  function ensureStyle() {
    if (document.getElementById(STYLE_ID)) return;
    var st = document.createElement('style');
    st.id = STYLE_ID;
    st.textContent =
      '.herbies-adding{cursor:progress!important;position:relative}' +
      '.herbies-adding .herbies-spinner{display:inline-block;width:1em;height:1em;margin-left:.5em;vertical-align:-0.15em;' +
      'border:2px solid currentColor;border-right-color:transparent;border-radius:50%;animation:herbies-spin .7s linear infinite}' +
      '@keyframes herbies-spin{to{transform:rotate(360deg)}}';
    document.head.appendChild(st);
  }

  function lockButton(btn) {
    if (!btn) return;
    ensureStyle();
    btn.dataset.herbiesAdding = '1';
    btn.dataset.styleSnapshot = btn.getAttribute('style') || '';
    btn.style.setProperty('pointer-events', 'none', 'important');
    btn.setAttribute('aria-busy', 'true');
    btn.classList.add('herbies-adding');
    if (!btn.querySelector('.herbies-spinner')) {
      var label = btn.querySelector('span') || btn; // next to the button text, not under it
      var sp = document.createElement('span');
      sp.className = 'herbies-spinner';
      sp.setAttribute('aria-hidden', 'true');
      label.appendChild(sp);
    }
  }

  function releaseButton(btn) {
    if (!btn) return;
    if (typeof btn.dataset.styleSnapshot === 'string') {
      if (btn.dataset.styleSnapshot === '') btn.removeAttribute('style');
      else btn.setAttribute('style', btn.dataset.styleSnapshot);
    }
    delete btn.dataset.styleSnapshot;
    delete btn.dataset.herbiesAdding;
    btn.removeAttribute('aria-busy');
    btn.classList.remove('herbies-adding');
    var sp = btn.querySelector('.herbies-spinner');
    if (sp) sp.remove();
  }

  function showError(form, message) {
    var box = (form && form.closest('product-info, .product__info-wrapper, section')) || document;
    var el = box.querySelector('.product-form__error-message');
    var wrap = box.querySelector('.product-form__error-message-wrapper');
    if (el) el.textContent = message;
    if (wrap) wrap.removeAttribute('hidden');
  }

  function wait(ms) { return new Promise(function (r) { setTimeout(r, ms); }); }

  function tickedSubscription() {
    return document.querySelector('.subscribe-save-wrapper input[type="checkbox"]:checked, .subscribe-save-wrapper-dynamic input[type="checkbox"]:checked');
  }

  function planFor(form) {
    var input = form.querySelector('[name="selling_plan"]');
    if (input && input.value) return input.value;
    var ctx = window.subscriptionContext;
    return (ctx && ctx.sellingPlanId) || '';
  }

  /**
   * The form, once the bundle and subscription scripts have filled in the
   * variant and, when the subscription toggle is ticked, the selling plan.
   * A click right after load can beat those scripts by a few hundred ms.
   */
  function readyForm(form) {
    var tries = 0;
    return new Promise(function (resolve) {
      (function check() {
        var f = form || document.querySelector('form[action*="/cart/add"].product-buy-form-pdp') || document.querySelector('form[action*="/cart/add"]');
        var id = f && f.querySelector('[name="id"]');
        var planReady = !tickedSubscription() || (f && planFor(f));
        if (f && id && id.value && planReady) return resolve(f);
        if (++tries > 16) return resolve(f || null);
        setTimeout(check, 150);
      })();
    });
  }

  /** The main line(s) from the selected bundle card and the form. */
  function mainItems(form) {
    var scope = form.closest('product-info, .product__info-wrapper, section') || document;
    var card = scope.querySelector('.quantity-bundle-card[data-selected="true"]') || document.querySelector('.quantity-bundle-card[data-selected="true"]');
    var variantId = Number((form.querySelector('[name="id"]') || {}).value);
    // Only when the toggle is ticked: the context can hold a plan while one-time is chosen.
    var sellingPlan = (form.querySelector('[name="selling_plan"]') || {}).value || (tickedSubscription() ? planFor(form) : '');
    var productId = form.getAttribute('data-product-id') || SETTINGS.productId || '';
    var total = card ? parseInt(card.getAttribute('data-quantity'), 10) : NaN;
    var paid = card ? parseInt(card.getAttribute('data-bogo-multiplier'), 10) : NaN;
    var bundleVariant = card ? Number(card.getAttribute('data-bundle-variant') || 0) : 0;
    var isSub = !!sellingPlan;

    if (bundleVariant) {
      // A bundle card with its own product (Detox Bundle): that product once, counted as the top tier.
      var host = { id: bundleVariant, quantity: 1, properties: { _bundle_host: String(productId), _bundle_tier: 'bundle3' } };
      if (sellingPlan) host.selling_plan = Number(sellingPlan);
      return { items: [host], productId: productId, qty: null, isSub: isSub };
    }
    if (variantId && card && paid > 0 && total > paid) {
      // A BOGO tier: paid units keep the subscription; the free units are a separate one-time line.
      var paidItem = { id: variantId, quantity: paid };
      if (sellingPlan) paidItem.selling_plan = Number(sellingPlan);
      var freeItem = { id: variantId, quantity: total - paid, properties: { _bundle_free: 'true', _bundle_min_paid: String(paid) } };
      return { items: [paidItem, freeItem], productId: productId, qty: total, isSub: isSub };
    }
    var qtyInput = form.querySelector('[name="quantity"]');
    var qty = Math.max(1, parseInt((qtyInput && qtyInput.value) || (card && card.getAttribute('data-quantity')) || '1', 10) || 1);
    var item = { id: variantId, quantity: qty };
    if (sellingPlan) item.selling_plan = Number(sellingPlan);
    return { items: [item], productId: productId, qty: qty, isSub: isSub };
  }

  function postAdd(items) {
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: items }),
    }).then(function (r) {
      return r.json().catch(function () { return {}; }).then(function (body) { return { ok: r.ok, status: r.status, body: body }; });
    });
  }

  /**
   * Free units and tier gifts, added to the same cart by the bundles app
   * (Storefront API, server-side). Shopify's browser tracking reports every
   * line the browser adds as an add-to-cart, so adding these here would turn
   * one click into 3-6 AddToCart events. If the app cannot do it in time the
   * browser adds them after all: the customer always gets the free items.
   */
  function addExtras(extras, productId) {
    if (!extras.length) return Promise.resolve();
    var viaApp = fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        return fetch(SETTINGS.apiBase + '/api/cart-extras', {
          method: 'POST',
          headers: { 'Content-Type': 'text/plain' }, // a simple request: no CORS preflight
          body: JSON.stringify({
            shop: SETTINGS.shop,
            cartToken: cart.token,
            productId: Number(productId),
            lines: extras.map(function (l) {
              return {
                variantId: l.id,
                quantity: l.quantity,
                attributes: Object.keys(l.properties || {}).map(function (k) { return { key: k, value: String(l.properties[k]) }; }),
              };
            }),
          }),
        });
      })
      .then(function (r) { if (!r.ok) throw new Error('cart-extras ' + r.status); return r.json(); });

    var timeout = new Promise(function (_, reject) { setTimeout(function () { reject(new Error('cart-extras timeout')); }, EXTRAS_WAIT_MS); });

    return Promise.race([viaApp, timeout]).catch(function (err) {
      try { console.warn('[herbies-cart] extras via the browser:', err && err.message); } catch (_e) { /* noop */ }
      // Only what is not in the cart yet: a slow app call may have landed after all,
      // and a second free-unit line would be charged.
      return fetch('/cart.js', { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          var missing = extras.filter(function (l) {
            var kind = l.properties && l.properties._bundle_gift ? '_bundle_gift' : '_bundle_free';
            return !(cart.items || []).some(function (i) { return i.variant_id === l.id && i.properties && i.properties[kind]; });
          });
          if (!missing.length) return;
          return postAdd(missing).then(function (r) {
            if (r.ok) return;
            // A sold-out gift must not cost the customer the free units.
            var freeOnly = missing.filter(function (l) { return !(l.properties && l.properties._bundle_gift); });
            if (freeOnly.length && freeOnly.length < missing.length) return postAdd(freeOnly);
          });
        })
        .catch(function () { /* the paid line is in; never block the checkout on extras */ });
    });
  }

  function goCheckout() {
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    window.location.href = root.replace(/\/+$/, '') + '/checkout';
  }

  function openDrawer(cartData) {
    var drawer = document.querySelector('cart-drawer');
    try {
      if (window.publish && window.PUB_SUB_EVENTS) window.publish(window.PUB_SUB_EVENTS.cartUpdate, { source: 'herbies-cart', cartData: cartData });
    } catch (_e) { /* noop */ }
    if (drawer && typeof drawer.refreshCartDrawer === 'function') drawer.refreshCartDrawer(null);
    else window.location.href = (window.routes && window.routes.cart_url) || '/cart';
  }

  /**
   * Add the selected bundle (with its gifts) in one request.
   * opts: { form, button, mode: 'checkout' | 'drawer' }
   */
  function add(opts) {
    opts = opts || {};
    var btn = opts.button || null;
    if (adding) return Promise.resolve(false);
    adding = true;
    lockButton(btn);

    return Promise.all([readyForm(opts.form), configWithin(CONFIG_WAIT_MS)])
      .then(function (res) {
        var form = res[0];
        var config = res[1];
        if (!form) throw new Error('no product form');
        var main = mainItems(form);
        if (!main.items.length || !main.items[0].id) throw new Error('no variant selected');

        var cfg = productConfig(config, main.productId);
        var qty = main.qty === null ? topTier(cfg) : main.qty;
        var gifts = giftIdsFor(cfg, qty, main.isSub).map(function (id) {
          return { id: Number(id), quantity: 1, properties: { _bundle_gift: '1' } };
        });

        // The paid line goes through /cart/add (the one AddToCart for this click);
        // free units and gifts are added server-side, see addExtras.
        var paid = [main.items[0]];
        var extras = main.items.slice(1).concat(gifts);

        var attempt = 0;
        function tryAdd() {
          attempt++;
          return postAdd(paid).then(function (r) {
            if (r.ok) return r.body;
            if (attempt < 3 && (r.status === 409 || r.status >= 500)) return wait(400).then(tryAdd);
            var err = new Error((r.body && (r.body.description || r.body.message)) || 'Could not add to cart');
            err.status = r.status;
            throw err;
          }, function (netErr) {
            if (attempt < 3) return wait(400).then(tryAdd);
            throw netErr;
          });
        }

        return tryAdd()
          .then(function (cartData) { return addExtras(extras, main.productId).then(function () { return cartData; }); })
          .then(function (cartData) {
          if (opts.mode === 'drawer') {
            adding = false;
            releaseButton(btn);
            openDrawer(cartData);
          } else {
            goCheckout(); // the button stays locked while we leave
          }
          return true;
        });
      })
      .catch(function (err) {
        adding = false;
        releaseButton(btn);
        showError(opts.form, (err && err.message) || 'Something went wrong, please try again.');
        try { console.warn('[herbies-cart]', err); } catch (_e) { /* noop */ }
        return false;
      });
  }

  // Back from checkout: the browser may restore this page from its cache with
  // the button still locked. Unlock it.
  window.addEventListener('pageshow', function (e) {
    if (!e.persisted) return;
    adding = false;
    document.querySelectorAll('[data-herbies-adding]').forEach(releaseButton);
  });

  // Start loading the config right away so a click rarely waits.
  if (document.readyState !== 'loading') loadConfig();
  else document.addEventListener('DOMContentLoaded', loadConfig);

  window.HerbiesCart = {
    add: add,
    loadConfig: loadConfig,
    eligibleGiftIds: eligibleGiftIds,
  };

  /* Backwards compatible entry point for the PDP buttons (onclick="skipToCheckout(event)"). */
  window.skipToCheckout = function (e) {
    if (e && e.preventDefault) e.preventDefault();
    var btn = (e && e.currentTarget) || null;
    var form = btn && btn.closest ? btn.closest('form') : null;
    return add({ form: form, button: btn, mode: 'checkout' });
  };
})();
